/**
 * 500 — rules options.
 *
 * Every field here is a variant the rules doc (docs/rules/500.md §9) says a real
 * ruleset disagrees about, with the recommended default baked into
 * DEFAULT_FH_CONFIG. Nothing that is universally agreed (10 tricks, contractor
 * leads trick 1, must-follow-suit) is configurable — that would just be a place
 * for bugs to hide.
 */
export const DEFAULT_FH_CONFIG = {
    deckVariant: '43',
    target: 500,
    allPassBehaviour: 'nextDealer',
    bidAfterPass: 'never',
    raiseOwnBid: false,
    misereAvailability: 'anytime',
    misereValue: 250,
    miserePlacement: 'after7NT',
    openMisereAvailability: 'anytime',
    openMisereValue: 500,
    openMiserePlacement: 'between10DAnd10H',
    slamBonus: true,
    slamBonusValue: 250,
    defenderTrickPoints: 10,
    misereGetsKitty: 'always',
    jokerLowInMisere: false,
    jokerCannotCallVoidSuit: false,
    allowJokerNomination: true,
    revealDiscardAtHandEnd: true,
};
/** Kitty size follows the deck: 43 ⇒ 3, 45 ⇒ 5 (rules §1.1, §9). */
export function kittySize(config) {
    return config.deckVariant === '45' ? 5 : 3;
}
export const FH_TRICKS_PER_HAND = 10;
const ONE_OF = (values) => (v) => typeof v === 'string' && values.includes(v);
const isDeckVariant = ONE_OF(['43', '45']);
const isMiserePlacement = ONE_OF(['after7NT', 'after8S']);
const isOpenPlacement = ONE_OF(['after9NT', 'between10DAnd10H', 'highest']);
const isBidAfterPass = ONE_OF(['never', 'always', 'ifBidChanged']);
const isAvailability = ONE_OF(['off', 'after7', 'anytime']);
const isMisereKitty = ONE_OF(['always', 'never', 'unlessOpen']);
/** Only 'nextDealer' is accepted — see the explicit rejection in
 *  `validateFhConfig` below for why 'sameDealer' is not. */
const isAllPass = ONE_OF(['nextDealer']);
/** Partial, forgiving merge over the defaults — a client may send only the
 *  fields it wants to change. Unknown keys are ignored rather than rejected so
 *  that adding an option later cannot break an old client. */
export function validateFhConfig(raw) {
    if (raw === undefined || raw === null)
        return { ok: true, config: DEFAULT_FH_CONFIG };
    if (typeof raw !== 'object')
        return { ok: false, error: 'config must be an object' };
    const r = raw;
    const out = { ...DEFAULT_FH_CONFIG };
    const str = (key, guard, allowed) => {
        if (r[key] === undefined)
            return null;
        if (!guard(r[key]))
            return `${key} must be one of ${allowed}`;
        out[key] = r[key];
        return null;
    };
    const num = (key, min, max) => {
        if (r[key] === undefined)
            return null;
        const v = r[key];
        if (typeof v !== 'number' || !Number.isFinite(v) || v < min || v > max) {
            return `${key} must be a number in [${min}, ${max}]`;
        }
        out[key] = v;
        return null;
    };
    const bool = (key) => {
        if (r[key] === undefined)
            return null;
        if (typeof r[key] !== 'boolean')
            return `${key} must be a boolean`;
        out[key] = r[key];
        return null;
    };
    // Checked before the generic guard so the message names the real reason.
    if (r.deckVariant === '46') {
        return { ok: false, error: 'the 46-card two-joker deck is not supported (core CardId has a single JOKER)' };
    }
    // VERDICT item 20: 'sameDealer' was declared here, validated here, and then
    // silently ignored — `onPassedOut` can only answer 'redeal' | 'score', and
    // the core's `nextHand` always rotates the dealer. A table that chose it
    // played the other variant and nothing said so, which over an evening is a
    // rules argument nobody can settle.
    //
    // Refusing is the honest answer until the core hook can carry the choice. A
    // proper fix is not local to this file: `GameModule.onPassedOut` would need
    // to return something like `{ kind: 'redeal'; dealer: 'same' | 'next' }`, the
    // reducer's throw-in path would have to thread that through `finishHand` to
    // the `nextHand` that follows, and the replay/persistence schema would have
    // to survive reading matches recorded before the field existed. That is a
    // change to `shared/src/core`, and it belongs with whoever owns that
    // boundary — not a special case smuggled into one game's validator.
    if (r.allPassBehaviour === 'sameDealer') {
        return {
            ok: false,
            error: "allPassBehaviour 'sameDealer' is not supported: the engine always moves the deal to the next dealer " +
                'after a throw-in. Use \'nextDealer\'.',
        };
    }
    const errors = [
        str('deckVariant', isDeckVariant, "'43' | '45'"),
        str('allPassBehaviour', isAllPass, "'nextDealer'"),
        str('bidAfterPass', isBidAfterPass, "'never' | 'always' | 'ifBidChanged'"),
        str('misereAvailability', isAvailability, "'off' | 'after7' | 'anytime'"),
        str('openMisereAvailability', isAvailability, "'off' | 'after7' | 'anytime'"),
        str('miserePlacement', isMiserePlacement, "'after7NT' | 'after8S'"),
        str('openMiserePlacement', isOpenPlacement, "'after9NT' | 'between10DAnd10H' | 'highest'"),
        str('misereGetsKitty', isMisereKitty, "'always' | 'never' | 'unlessOpen'"),
        num('target', 100, 10000),
        num('misereValue', 10, 2000),
        num('openMisereValue', 10, 2000),
        num('slamBonusValue', 0, 2000),
        num('defenderTrickPoints', 0, 100),
        bool('raiseOwnBid'),
        bool('slamBonus'),
        bool('jokerLowInMisere'),
        bool('jokerCannotCallVoidSuit'),
        bool('allowJokerNomination'),
        bool('revealDiscardAtHandEnd'),
    ].filter((e) => e !== null);
    if (errors.length > 0)
        return { ok: false, error: errors.join('; ') };
    return { ok: true, config: out };
}
//# sourceMappingURL=config.js.map